/** @jsxImportSource @emotion/react */
import React from 'react';
import css from '@emotion/css';
import _Select from 'react-select/async';
import { components } from 'react-select';
import _Chip from '@material-ui/core/Chip';
import CancelIcon from '@material-ui/icons/Cancel';
import InputBase from '../form/InputBase';
import styled from 'styled-components/macro';
import { SimpleFlexContainer } from '../layouts/ng-layouts/Containers';
import Button from '../Button';
import useTranslations from '../../hooks/useTranslations';
import WarningDialog from '../../internal-components/GeneralDialog';
import Animation from '../Animation';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Colors from '../../internal-components/Colors';
import MuiIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';

const ButtonPositioner = styled.span`
  align-self: flex-end;
  padding: 0.8rem 0 0;
`;

const SubTitle = styled.h3`
  color: #1e202d; // same as h2
  font-weight: 500;
  margin: 0.8rem 0;
`;

const EnrolledGroupList = styled(SimpleFlexContainer)``;

const EnrolledGroupListItem = styled(Animation)`
  div[data-id='animation-container'] {
    transition: 0.25s;
    border-bottom: 1px solid
      ${({ $isBeingHoved }) =>
        $isBeingHoved ? Colors.BORDER_GREY : 'rgba(0, 0, 0, 0.0);'};
    display: flex;
    align-items: center;
    button {
      margin-left: auto;
    }
  }
`;

const IconButton = styled(MuiIconButton)`
  && {
    transition: 0.25s;
    opacity: ${({ $isBeingHoved }) => ($isBeingHoved ? '1' : '0')};
  }
`;

const GroupsIconContainer = styled.div`
  border-radius: 20px;
  background-color: ${Colors.ICON_GREY};
  padding: 4px;
  display: flex;
  margin-right: 0.8rem;

  svg {
    width: 16px;
    height: 16px;
    color: #fff;
  }
`;

const Select = styled(_Select)`
  && {
    font-size: 14px;
  }
`;

const Control = props => {
  let propsWithTestId = {
    ...props,
    innerProps: {
      ...props.innerProps,
      'data-testid': 'groups-and-users-select'
    }
  };
  return (
    <components.Control {...propsWithTestId} />
  );
};

const GroupsAndUsersInputBoxView = props => {
  const t = useTranslations();
  return (
    <>
      <SimpleFlexContainer>
        <Select
          value={props.recipients}
          onChange={props.onRecipientsChange}
          cacheOptions
          components={{
            Control,
            Option: optionProps => {
              const {
                children,
                className,
                cx,
                data,
                getStyles,
                isDisabled,
                isFocused,
                isSelected,
                innerRef,
                innerProps,
                label
              } = optionProps;
              const userTypeLabel = data.schema === 'external' ? 'External user' : 'Internal user';
              return (
                <div 
                  ref={innerRef}
                  css={getStyles('option', optionProps)}
                  className={cx(
                    {
                      option: true,
                      'option--is-disabled': isDisabled,
                      'option--is-focused': isFocused,
                      'option--is-selected': isSelected,
                    },
                    className
                  )}
                  {...innerProps}
                  {...props.innerProps}
                >
                  <span>
                    {label}
                    {optionProps.data.schema && props.allowExternalUserAccounts ? (
                    <>
                      <UserTypeIcon>
                        &nbsp;&nbsp;&nbsp;◀●▶
                        &nbsp;&nbsp;&nbsp;
                      </UserTypeIcon>
                      {userTypeLabel}
                    </>
                    ) : ''}
                  </span>
                </div>
              );
            },
            MultiValue,
            valueContainer: InputBase,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
          }}
          loadOptions={props.loadOptions}
          openMenuOnClick={false}
          isMulti
          noOptionsMessage={e =>
            e.inputValue
              ? t('form-builder.share.create-submissions.no-results')
              : null
          }
          placeholder={t('form-builder.share.create-submissions.placeholder')}
          id="groups-and-users-select"
          key={props.cacheKiller}
        />
        <Animation type="collapse" in={props.showAutoEnrollCheckbox}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={props.isAutoEnrollChecked}
                onChange={e => props.setIsAutoEnrollChecked(e.target.checked)}
                data-testid="auto-enroll"
                size="small"
              />
            }
            label={<small>{t('form-builder.share.auto-enroll')}</small>}
          />
        </Animation>
        <ButtonPositioner>
          <Button
            disabled={!props.recipients || !props.recipients.length}
            onClick={() => props.setIsBeforeSendWarningDialogOpen(true)}
            size="small"
            data-testid="create-submissions-btn"
          >
            {t('form-builder.share.emails.button')}
          </Button>
        </ButtonPositioner>
        <Animation
          type="collapse"
          in={
            props.autoEnrollGroups &&
            Object.values(props.autoEnrollGroups).length
          }
        >
          <SubTitle>{t('form-builder.share.enrolled-groups.title')}</SubTitle>
          <EnrolledGroupList>
            {Object.values(props.autoEnrollGroups).map(group => (
              <EnrolledGroupListItem
                key={group.id}
                type="collapse"
                in={props.inDeleteSequence !== group.id}
                onExited={() =>
                  props.dispatch({
                    type: 'delete_update_autoEnrollGroup',
                    id: group.id
                  })
                }
                onMouseEnter={() => props.setListItemInHover(group.id)}
                onMouseLeave={() => props.setListItemInHover('')}
                $isBeingHoved={props.listItemInHover === group.id}
              >
                <GroupsIconContainer>
                  <PeopleAltIcon />
                </GroupsIconContainer>

                <span>{group.label}</span>
                <Tooltip
                  arrow
                  title={t('form-builder.share.enrolled-groups.delete.tooltip')}
                >
                  <IconButton
                    size="small"
                    onClick={() => props.setGroup2Remove(group.id)}
                    $isBeingHoved={props.listItemInHover === group.id}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </EnrolledGroupListItem>
            ))}
          </EnrolledGroupList>
        </Animation>
      </SimpleFlexContainer>
      <WarningDialog
        title="dialog.title.areYouSure"
        content="form-builder.share.before-send-dialog.content"
        open={props.isBeforeSendWarningDialogOpen}
        handleClose={() => props.setIsBeforeSendWarningDialogOpen(false)}
        handleConfirm={props.onEmailsSend}
      />
      <WarningDialog
        title="dialog.title.areYouSure"
        content="form-builder.share.before-delete.content"
        open={!!props.group2Remove}
        handleClose={() => props.setGroup2Remove(null)}
        handleConfirm={props.deleteAutoEnrollGroup}
      />
    </>
  );
};

const Chip = styled(_Chip)`
  && {
    margin-right: 0.2rem;
  }
`;

const UserTypeIcon = styled.span`
  color: ${Colors.BORDER_GREY};
  font-size: 11px;
`;

export const MultiValue = props => {
  return (
    <Chip
      size="small"
      tabIndex={-1}
      label={props.children}
      onDelete={props.removeProps.onClick}
      color="primary"
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
};

export default GroupsAndUsersInputBoxView;
